import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardActionArea, CardContent,
  Typography, Chip, Box, Stack,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  CleaningServices as ServiceIcon,
  ChecklistRtl as ChecklistIcon,
  ChevronRight as ChevronIcon,
  Payments as PayoutIcon,
} from '@mui/icons-material';
import PropertyInfoDisplay from './PropertyInfoDisplay';

const STATUS_CONFIG = {
  NOT_STARTED: { label: 'Not Started', color: 'warning' },
  IN_PROGRESS: { label: 'In Progress', color: 'info' },
  COMPLETED:   { label: 'Completed',   color: 'success' },
  CANCELLED:   { label: 'Cancelled',   color: 'error' },
};

const formatTime = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

/**
 * MaidJobCard — queue tile.
 *
 * Lean by design: just the at-a-glance info the maid needs in her queue.
 * Tapping the card navigates to the full JobExecutionScreen where all
 * in-job actions (start, checklist, complete, and Phase A steps) live.
 *
 * Why no inline checklist? It bloated the queue and split the "doing the job"
 * UX across two places. One detail screen per job, one place to act.
 */
const MaidJobCard = ({ job }) => {
  const navigate = useNavigate();
  const statusCfg = STATUS_CONFIG[job.status] || { label: job.status, color: 'default' };

  const customerName =
    job.customer?.customerName ||
    `${job.customer?.firstName || ''} ${job.customer?.lastName || ''}`.trim() ||
    'Customer';

  const address = job.customer?.customerAddress || job.serviceAddress || null;
  const scheduledDate = formatDate(job.scheduledStartTime || job.utcScheduledDate);
  const startTime = formatTime(job.scheduledStartTime || job.utcScheduledDate);
  const endTime = formatTime(job.scheduledEndTime);

  // Prefer denormalized counts from Job model; fall back to tasks array if present.
  const totalTasks = job.totalTasks ?? (Array.isArray(job.tasks) ? job.tasks.length : null);
  const completedTasks = job.completedTasks ?? (Array.isArray(job.tasks)
    ? job.tasks.filter((t) => t.isCompleted).length
    : null);

  const serviceLabel = (job.serviceType || job.jobType || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardActionArea onClick={() => navigate(`/maid-portal/jobs/${job.id}`)}>
        <CardContent sx={{ pb: 1.5 }}>
          {/* Top row: customer + status */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              {customerName}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
              <ChevronIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
            </Box>
          </Box>

          {/* Meta rows */}
          <Stack spacing={0.5}>
            {scheduledDate && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <CalendarIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {scheduledDate}
                </Typography>
              </Box>
            )}

            {(startTime || endTime) && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <TimeIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {startTime}{endTime ? ` – ${endTime}` : ''}
                </Typography>
              </Box>
            )}

            {address && (
              <Box display="flex" alignItems="flex-start" gap={0.5}>
                <LocationIcon sx={{ fontSize: 15, color: 'text.secondary', mt: '2px' }} />
                <Typography variant="body2" color="text.secondary">{address}</Typography>
              </Box>
            )}

            {/* Property info chips (no photo — queue tile stays lean) */}
            <PropertyInfoDisplay propertyInfo={job.propertyInfo} photo="none" />

            {serviceLabel && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <ServiceIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">{serviceLabel}</Typography>
              </Box>
            )}

            {job.myAssignment?.paymentAmount != null && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <PayoutIcon sx={{ fontSize: 15, color: 'success.main' }} />
                <Typography variant="body2" fontWeight={600} color="success.main">
                  Your payout ${Number(job.myAssignment.paymentAmount).toFixed(2)}
                </Typography>
              </Box>
            )}

            {totalTasks != null && totalTasks > 0 && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <ChecklistIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {completedTasks ?? 0} / {totalTasks} tasks
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default MaidJobCard;
